import { isEqual } from "@common/helpers/comparison";
import { parseDateOnlyAsUTC } from "@common/helpers/date";
import { omitBy } from "@common/helpers/objects";
import { RowSelectionState } from "@tanstack/react-table";
import { useCallback, useMemo, useRef, useState } from "react";
import { formatISO } from "date-fns";
import {
    FinancialAccount,
    FinancialAccountSourceType,
} from "@common/types/financialAccount";
import {
    Invoice,
    InvoiceLineItem,
    InvoiceSource,
    InvoiceStatus,
    InvoiceViewOptionGroup,
} from "@common/types/invoice";
import { DateOnlyDateRange } from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useIsClassesEnabled } from "@hooks/useIsClassesEnabled";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useIsRecognitionScheduleColumnEnabled } from "@hooks/useIsRecognitionScheduleColumnEnabled";
import { useLineItemsBulkActions } from "@hooks/useLineItemsBulkActions";
import { useEntities } from "@hooks/useEntities";
import { useBulkAccrualAccountEntityId } from "@hooks/useBulkAccrualAccountEntityId";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useConstant } from "@hooks/useConstant";
import {
    InvoiceColumn,
    InvoiceLineItemColumn,
    InvoiceLineItemView,
    InvoicesByCustomerView,
    InvoiceView,
    InvoiceLineItemViewSchema,
    InvoicesByCustomerViewSchema,
    InvoiceViewSchema,
    migrateInvoicesByCustomerColumns,
} from "@common/types/views/invoiceViews";
import { ensureRequiredColumns } from "@hooks/view/ensureRequiredColumns";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import {
    getBillcomNewInvoiceUrl,
    getStripeDashboardUrlForFinancialAccount,
    isInvoiceProviderAccount,
} from "@helpers/invoice";
import { DATE_RANGE_PRESETS_BY_ID } from "@helpers/date";
import { useScope } from "@state/scopeContext.lib";
import {
    useViewChangeHandler,
    useViewSelectHandler,
} from "@hooks/view/useViewChangeHandler";
import { useOverlays } from "contexts/OverlayContext";
import {
    useBulkUpdateInvoiceLineItems,
    useUpdateInvoice,
} from "@api/invoice.api";
import { useHasAnyAccrualLedger } from "@api/ledger.api";
import { useMultiLedgerColumns } from "@hooks/useMultiLedgerColumns";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import {
    defaultInvoiceStatuses,
    FiltersChangeFn,
} from "../pageHeader/filters/lib";
import { useInvoicesView } from "../../contexts/InvoicesViewContext";
import { CreateAccountHandle } from "../accounts/AddAccounts/lib";
import {
    defaultInvoiceLineItemsView,
    defaultInvoicesByCustomerView,
    defaultInvoicesView,
} from "./views/defaultInvoiceViews";
import { createInvoiceLineItemsViewConfig } from "./views/invoiceLineItemsView/invoiceLineItemsViewConfig";
import {
    useBuiltInInvoiceViews,
    useBuiltInInvoiceLineItemViews,
    useBuiltInInvoicesByCustomerViews,
} from "./views/BuiltInInvoiceViews";

export const useInvoicePage = (invoicingAccounts: FinancialAccount[]) => {
    const { error } = useToaster();
    const [selectedInvoice, setSelectedInvoice] = useState<Invoice>();
    const [showAccountSelectModal, setShowAccountSelectModal] = useState(false);
    const [accountSelectAccounts, setAccountSelectAccounts] = useState<
        FinancialAccount[]
    >([]);
    const [cachedInvoiceTableView, setCachedInvoiceTableView] =
        useWorkspaceUserCache<InvoiceView>(
            WORKSPACE_USER_CACHE_KEYS.INVOICES_PAGE_INVOICE_VIEW_CACHE_KEY,
            InvoiceViewSchema,
        );
    const [
        cachedInvoiceLineItemsTableView,
        setCachedInvoiceLineItemsTableView,
    ] = useWorkspaceUserCache<InvoiceLineItemView>(
        WORKSPACE_USER_CACHE_KEYS.INVOICES_PAGE_LINE_ITEMS_VIEW_CACHE_KEY,
        InvoiceLineItemViewSchema,
    );
    const [
        cachedInvoicesByCustomerTableView,
        setCachedInvoicesByCustomerTableView,
    ] = useWorkspaceUserCache<InvoicesByCustomerView>(
        WORKSPACE_USER_CACHE_KEYS.INVOICES_PAGE_BY_CUSTOMER_VIEW_CACHE_KEY,
        InvoicesByCustomerViewSchema,
    );
    const [invoiceTableView, setInvoiceTableView] = useState<InvoiceView>(() =>
        cachedInvoiceTableView
            ? {
                  ...cachedInvoiceTableView,
                  columns: ensureRequiredColumns(
                      cachedInvoiceTableView.columns,
                      defaultInvoicesView.columns,
                      [InvoiceColumn.LOCK],
                  ),
              }
            : defaultInvoicesView,
    );
    const [invoiceLineItemsTableView, setInvoiceLineItemsTableView] =
        useState<InvoiceLineItemView>(() =>
            cachedInvoiceLineItemsTableView
                ? {
                      ...cachedInvoiceLineItemsTableView,
                      columns: ensureRequiredColumns(
                          cachedInvoiceLineItemsTableView.columns,
                          defaultInvoiceLineItemsView.columns,
                          [
                              InvoiceLineItemColumn.LOCK,
                              InvoiceLineItemColumn.ACCRUAL_ACCOUNT,
                          ],
                      ),
                  }
                : defaultInvoiceLineItemsView,
        );
    const [
        baseInvoicesByCustomerTableView,
        setBaseInvoicesByCustomerTableView,
    ] = useState<InvoicesByCustomerView>(
        cachedInvoicesByCustomerTableView ?? defaultInvoicesByCustomerView,
    );
    // Translate legacy aging column ids (notYetDue / pastDue61Plus) so a
    // server-saved layout keeps its columns instead of the table silently
    // dropping them. The cache path is already migrated by
    // InvoicesByCustomerViewSchema; this covers views selected at runtime.
    const invoicesByCustomerTableView = useMemo<InvoicesByCustomerView>(
        () => ({
            ...baseInvoicesByCustomerTableView,
            columns: migrateInvoicesByCustomerColumns(
                baseInvoicesByCustomerTableView.columns,
            ),
        }),
        [baseInvoicesByCustomerTableView],
    );

    const { manualAccrualCreation: isManualAccrualCreationEnabled } =
        useTypedFlags();
    const isSchedulesEnabled = useIsRecognitionScheduleColumnEnabled();
    const isClassesEnabled = useIsClassesEnabled();
    const { openOverlay } = useOverlays();
    const entities = useEntities({ onlyBusiness: true });
    const entityIds = useMemo(
        () => entities.map((entity) => entity.id),
        [entities],
    );
    const hasAnyAccrualLedgers = useHasAnyAccrualLedger(entityIds);
    const hasVisibleAccrualLedgerColumn = hasAnyAccrualLedgers;
    const multiLedgerLedgers = useMultiLedgerColumns(entityIds, {
        basis: AccountingLedgerBasis.accruals,
        uniqueByBasisAndName: true,
    });
    const invoiceLineItemsViewConfig = useMemo(
        () =>
            createInvoiceLineItemsViewConfig({
                hasAnyAccrualLedgers: hasVisibleAccrualLedgerColumn,
                multiLedgerLedgers,
                isSchedulesEnabled,
                isClassesEnabled,
            }),
        [
            hasVisibleAccrualLedgerColumn,
            multiLedgerLedgers,
            isSchedulesEnabled,
            isClassesEnabled,
        ],
    );
    const { workspaceId } = useScope();
    const bulkUpdateInvoiceLineItems = useBulkUpdateInvoiceLineItems();
    const updateInvoiceMutation = useUpdateInvoice();

    const handleUpdateInvoiceMemo = useCallback(
        async (memo: string | null) => {
            if (!workspaceId || !selectedInvoice) {
                return;
            }
            await updateInvoiceMutation.mutateAsync({
                workspaceId,
                invoiceId: selectedInvoice.id,
                memo,
            });
        },
        [workspaceId, selectedInvoice, updateInvoiceMutation],
    );

    const handleUpdateInvoiceDueDate = useCallback(
        async (due: string | null) => {
            if (!workspaceId || !selectedInvoice) {
                return;
            }
            await updateInvoiceMutation.mutateAsync({
                workspaceId,
                invoiceId: selectedInvoice.id,
                due,
            });
            setSelectedInvoice({
                ...selectedInvoice,
                due: due ? parseDateOnlyAsUTC(due) : null,
            });
        },
        [workspaceId, selectedInvoice, updateInvoiceMutation],
    );

    const handleUpdateInvoiceStatus = useCallback(
        async ({
            status,
            uncollectableAt,
            voidedAt,
            paidAt,
        }: {
            status: InvoiceStatus;
            uncollectableAt?: string | null;
            voidedAt?: string | null;
            paidAt?: string | null;
        }) => {
            if (!workspaceId || !selectedInvoice) {
                return;
            }
            await updateInvoiceMutation.mutateAsync({
                workspaceId,
                invoiceId: selectedInvoice.id,
                status,
                uncollectableAt,
                voidedAt,
                paidAt,
            });
            setSelectedInvoice({
                ...selectedInvoice,
                status,
                ...(uncollectableAt !== undefined && { uncollectableAt }),
                ...(voidedAt !== undefined && { voidedAt }),
                ...(paidAt !== undefined && {
                    paidAt: paidAt ? new Date(paidAt) : null,
                }),
            });
        },
        [workspaceId, selectedInvoice, updateInvoiceMutation],
    );

    const viewOptions = useMemo(
        () => [
            InvoiceViewOptionGroup.INVOICE,
            InvoiceViewOptionGroup.COUNTERPARTY,
            InvoiceViewOptionGroup.LINE_ITEMS,
        ],
        [],
    );

    const builtInInvoiceViews = useBuiltInInvoiceViews();
    const builtInInvoiceLineItemViews = useBuiltInInvoiceLineItemViews();
    const builtInInvoicesByCustomerViews = useBuiltInInvoicesByCustomerViews();

    const {
        filters,
        setFilters,
        viewOption,
        setViewOption,
        invoiceStats,
        initialIds,
    } = useInvoicesView();

    const {
        selectedLineItems: selectedInvoiceLineItems,
        hasSelectedByCurrentFilters,
        selectedCount: selectedInvoiceLineItemsCount,
        clearLineItemsSelection,
        handleSelectedLineItemsChange,
        selectAllByCurrentFilters,
        handleBulkClassesSelected,
        handleBulkAccrualAccountSelected,
        handleBulkAccountsReceivableSelected,
    } = useLineItemsBulkActions<InvoiceLineItem, typeof filters>({
        workspaceId,
        filters,
        onBulkUpdate: bulkUpdateInvoiceLineItems.mutateAsync,
        onError: error,
    });

    const getInvoiceFinancialAccountId = useCallback(
        (lineItem: InvoiceLineItem) => lineItem.invoice?.financialAccountId,
        [],
    );
    const getInvoiceEntityId = useCallback(
        (lineItem: InvoiceLineItem) => lineItem.invoice?.entityId,
        [],
    );
    const bulkAccrualAccountEntityId = useBulkAccrualAccountEntityId({
        filters,
        hasSelectedByCurrentFilters,
        selectedLineItems: selectedInvoiceLineItems,
        getFinancialAccountId: getInvoiceFinancialAccountId,
        getEntityId: getInvoiceEntityId,
    });

    const [invoiceRowSelection, setInvoiceRowSelection] =
        useState<RowSelectionState>({});
    const [editInvoices, setEditInvoices] = useState<Invoice[] | null>(null);

    const clearInvoiceSelection = useCallback(() => {
        setInvoiceRowSelection({});
    }, []);

    const stripeAccounts = useMemo(
        () =>
            invoicingAccounts.filter(
                (a) => a.sourceType === FinancialAccountSourceType.STRIPE,
            ),
        [invoicingAccounts],
    );
    const hasStripeAccounts = stripeAccounts.length > 0;
    const stripeConnectionRef = useRef<CreateAccountHandle>(null);
    const mercuryConnectionRef = useRef<CreateAccountHandle>(null);
    const billcomConnectionRef = useRef<CreateAccountHandle>(null);
    const hasMercuryAccounts = useMemo(
        () =>
            invoicingAccounts.some(
                (a) =>
                    a.sourceType === FinancialAccountSourceType.MERCURY &&
                    isInvoiceProviderAccount(a),
            ),
        [invoicingAccounts],
    );
    const hasBillcomAccounts = useMemo(
        () =>
            invoicingAccounts.some(
                (a) => a.sourceType === FinancialAccountSourceType.BILLCOM,
            ),
        [invoicingAccounts],
    );

    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const getCreateInvoiceUrl = useCallback(
        (account: FinancialAccount): string | undefined => {
            if (account.sourceType === FinancialAccountSourceType.BILLCOM) {
                return getBillcomNewInvoiceUrl();
            }
            return getStripeDashboardUrlForFinancialAccount(account);
        },
        [],
    );

    const onCreateBillcomInvoice = useCallback(() => {
        // The next step leaves the app for the provider, so the server never
        // gets to refuse it.
        if (refuseIfViewOnly()) {
            return;
        }

        if (!hasBillcomAccounts) {
            billcomConnectionRef.current?.initializeConnection();
            return;
        }

        window.open(getBillcomNewInvoiceUrl(), "_blank", "noopener,noreferrer");
    }, [hasBillcomAccounts, refuseIfViewOnly]);

    const openCreateInvoiceForAccounts = useCallback(
        (accounts: FinancialAccount[]) => {
            if (accounts.length === 1) {
                const url = getCreateInvoiceUrl(accounts[0]);
                if (url) {
                    window.open(url, "_blank");
                }
                return;
            }

            setAccountSelectAccounts(accounts);
            setShowAccountSelectModal(true);
        },
        [getCreateInvoiceUrl],
    );

    const onCreateStripeInvoice = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }

        if (!hasStripeAccounts) {
            stripeConnectionRef.current?.initializeConnection();
            return;
        }

        openCreateInvoiceForAccounts(stripeAccounts);
    }, [
        hasStripeAccounts,
        stripeAccounts,
        openCreateInvoiceForAccounts,
        refuseIfViewOnly,
    ]);

    const onAccountSelect = useCallback(
        (account: FinancialAccount) => {
            setShowAccountSelectModal(false);
            const url = getCreateInvoiceUrl(account);
            if (url) {
                window.open(url, "_blank");
            }
        },
        [setShowAccountSelectModal, getCreateInvoiceUrl],
    );

    const onInvoiceClick = useCallback(
        (invoice: Invoice) => {
            setSelectedInvoice((previousInvoice) => {
                if (previousInvoice?.id === invoice.id) {
                    return undefined;
                }
                return invoice;
            });
        },
        [setSelectedInvoice],
    );

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (value) => {
            const newFilters = omitBy(
                { ...filters, ...value },
                (v) => v === null || v === undefined,
            );

            if (isEqual(newFilters, filters)) {
                return;
            }

            setFilters(newFilters);
            setSelectedInvoice(undefined);
            clearLineItemsSelection();
            clearInvoiceSelection();
        },
        [clearLineItemsSelection, clearInvoiceSelection, filters, setFilters],
    );

    const handleDateRangeChange = useCallback(
        (value: DateOnlyDateRange, isAllTime?: boolean) => {
            if (isAllTime) {
                handleFiltersChange({
                    createdOnFrom: undefined,
                    createdOnTo: formatISO(new Date(), {
                        representation: "date",
                    }),
                });
            } else {
                handleFiltersChange({
                    createdOnFrom: value.start,
                    createdOnTo: value.end,
                });
            }
        },
        [handleFiltersChange],
    );

    const today = useConstant(() => new Date());
    const defaultFilters = useMemo(() => {
        const last90DaysPreset = DATE_RANGE_PRESETS_BY_ID.last_90_days;
        return {
            externalInvoiceStatuses: defaultInvoiceStatuses,
            createdOnFrom: formatISO(last90DaysPreset.getStart(today), {
                representation: "date",
            }),
            createdOnTo: formatISO(last90DaysPreset.getEnd(today), {
                representation: "date",
            }),
        };
    }, [today]);

    const dateRange = useMemo(() => {
        if (
            filters?.createdOnFrom === undefined &&
            filters?.createdOnTo === undefined
        ) {
            return {
                start: defaultFilters.createdOnFrom,
                end: defaultFilters.createdOnTo,
            };
        }

        // All time is stored as "no createdOnFrom, has createdOnTo" (see handleDateRangeChange).
        // Do not compare `createdOnTo` to today — that breaks across midnight / persisted state.
        if (
            filters?.createdOnFrom === undefined &&
            filters?.createdOnTo !== undefined
        ) {
            return {
                start: undefined,
                end: undefined,
            };
        }

        return {
            start: filters?.createdOnFrom,
            end: filters?.createdOnTo,
        };
    }, [filters, defaultFilters]);

    const handleViewOptionChange = useCallback(
        (value: InvoiceViewOptionGroup) => {
            setViewOption({ groupBy: value });
            clearLineItemsSelection();
            clearInvoiceSelection();

            if (value === InvoiceViewOptionGroup.COUNTERPARTY) {
                setSelectedInvoice(undefined);
            }
        },
        [
            clearLineItemsSelection,
            clearInvoiceSelection,
            setViewOption,
            setSelectedInvoice,
        ],
    );

    const handleLineItemsEditClick = useCallback(() => {
        const lineItems = Object.values(selectedInvoiceLineItems);
        const uniqueInvoices = new Map<string, Invoice>();
        for (const lineItem of lineItems) {
            if (lineItem.invoice) {
                uniqueInvoices.set(lineItem.invoice.id, lineItem.invoice);
            }
        }
        setEditInvoices(Array.from(uniqueInvoices.values()));
    }, [selectedInvoiceLineItems]);

    const isLineItemsEditAvailable = useMemo(() => {
        if (!isManualAccrualCreationEnabled || hasSelectedByCurrentFilters) {
            return false;
        }
        const lineItems = Object.values(selectedInvoiceLineItems);
        if (lineItems.length === 0) {
            return false;
        }
        return lineItems.every(
            (li) => li.invoice?.source === InvoiceSource.IMPORTER,
        );
    }, [
        isManualAccrualCreationEnabled,
        hasSelectedByCurrentFilters,
        selectedInvoiceLineItems,
    ]);

    const handleInvoiceViewChange = useViewChangeHandler({
        setView: setInvoiceTableView,
        setCachedView: setCachedInvoiceTableView,
    });

    const handleInvoiceViewSelect = useViewSelectHandler({
        setView: setInvoiceTableView,
        setCachedView: setCachedInvoiceTableView,
        setFilters,
        defaultFilters,
    });

    const handleInvoiceLineItemsViewChange = useViewChangeHandler({
        setView: setInvoiceLineItemsTableView,
        setCachedView: setCachedInvoiceLineItemsTableView,
    });

    const handleInvoiceLineItemsViewSelect = useViewSelectHandler({
        setView: setInvoiceLineItemsTableView,
        setCachedView: setCachedInvoiceLineItemsTableView,
        setFilters,
        defaultFilters,
    });

    const handleInvoicesByCustomerViewChange = useViewChangeHandler({
        setView: setBaseInvoicesByCustomerTableView,
        setCachedView: setCachedInvoicesByCustomerTableView,
    });

    const handleInvoicesByCustomerViewSelect = useViewSelectHandler({
        setView: setBaseInvoicesByCustomerTableView,
        setCachedView: setCachedInvoicesByCustomerTableView,
        setFilters,
        defaultFilters,
    });

    const handleClearFilters = useCallback(() => {
        setFilters(defaultFilters);
        setSelectedInvoice(undefined);
        clearLineItemsSelection();
        clearInvoiceSelection();
    }, [
        clearInvoiceSelection,
        clearLineItemsSelection,
        defaultFilters,
        setFilters,
    ]);

    return {
        selectedInvoice,
        setSelectedInvoice,
        showAccountSelectModal,
        setShowAccountSelectModal,
        accountSelectAccounts,
        invoiceTableView,
        invoiceLineItemsTableView,
        invoicesByCustomerTableView,
        isManualAccrualCreationEnabled,
        openOverlay,
        hasVisibleAccrualLedgerColumn,
        invoiceLineItemsViewConfig,
        bulkUpdateInvoiceLineItems,
        handleUpdateInvoiceMemo,
        handleUpdateInvoiceStatus,
        handleUpdateInvoiceDueDate,
        viewOptions,
        builtInInvoiceViews,
        builtInInvoiceLineItemViews,
        builtInInvoicesByCustomerViews,
        filters,
        setFilters,
        viewOption,
        invoiceStats,
        initialIds,
        selectedInvoiceLineItems,
        hasSelectedByCurrentFilters,
        selectedInvoiceLineItemsCount,
        clearLineItemsSelection,
        handleSelectedLineItemsChange,
        selectAllByCurrentFilters,
        handleBulkClassesSelected,
        handleBulkAccrualAccountSelected,
        handleBulkAccountsReceivableSelected,
        bulkAccrualAccountEntityId,
        invoiceRowSelection,
        setInvoiceRowSelection,
        editInvoices,
        setEditInvoices,
        clearInvoiceSelection,
        stripeAccounts,
        hasStripeAccounts,
        stripeConnectionRef,
        mercuryConnectionRef,
        billcomConnectionRef,
        hasMercuryAccounts,
        hasBillcomAccounts,
        onCreateStripeInvoice,
        onCreateBillcomInvoice,
        onAccountSelect,
        onInvoiceClick,
        handleFiltersChange,
        handleDateRangeChange,
        defaultFilters,
        dateRange,
        handleViewOptionChange,
        handleLineItemsEditClick,
        isLineItemsEditAvailable,
        handleInvoiceViewChange,
        handleInvoiceViewSelect,
        handleInvoiceLineItemsViewChange,
        handleInvoiceLineItemsViewSelect,
        handleInvoicesByCustomerViewChange,
        handleInvoicesByCustomerViewSelect,
        handleClearFilters,
    };
};
